import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import toast from 'react-hot-toast';

import { getAnalyses, deleteAnalysis } from '../services/api';
import { formatToIST, formatTimeAgoIST } from '../utils/timeUtils'; // Add this import

const History = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analysisToDelete, setAnalysisToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    averageSpeed: 0,
    fastest: 0,
  });
  
  const theme = useTheme();

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const data = await getAnalyses(100, 0); // Get first 100 analyses
      setAnalyses(data.analyses || []);
      
      // Calculate stats
      if (data.analyses && data.analyses.length > 0) {
        const speeds = data.analyses
          .flatMap(analysis => 
            analysis.deliveries?.map(d => d.metrics?.speed_kmh) || []
          )
          .filter(speed => speed && !isNaN(speed));
        
        const totalSpeed = speeds.reduce((sum, speed) => sum + speed, 0);
        const averageSpeed = speeds.length > 0 ? totalSpeed / speeds.length : 0;
        const fastest = Math.max(...speeds, 0);
        
        setStats({
          total: data.total || 0,
          averageSpeed: Math.round(averageSpeed * 10) / 10,
          fastest: Math.round(fastest * 10) / 10,
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching analyses:', err);
      setError('Failed to load analysis history');
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (analysis) => {
    setAnalysisToDelete(analysis);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!analysisToDelete) return;
    
    try {
      await deleteAnalysis(analysisToDelete.analysis_id);
      toast.success('Analysis deleted successfully');
      fetchAnalyses(); // Refresh the list
    } catch (err) {
      console.error('Error deleting analysis:', err);
      toast.error('Failed to delete analysis');
    } finally {
      setDeleteDialogOpen(false);
      setAnalysisToDelete(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDeliveryTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'fast': return theme.palette.error.main;
      case 'fast_medium': return theme.palette.warning.main;
      case 'medium': return theme.palette.success.main;
      case 'spin': return theme.palette.info.main;
      default: return theme.palette.text.secondary;
    }
  };

  const filteredAnalyses = analyses.filter(analysis => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      analysis.filename?.toLowerCase().includes(searchLower) ||
      analysis.analysis_id?.toLowerCase().includes(searchLower) ||
      analysis.deliveries?.some(delivery => 
        delivery.delivery_type?.toLowerCase().includes(searchLower) ||
        delivery.pitch_impact?.length_category?.toLowerCase().includes(searchLower)
      )
    );
  });

  const paginatedAnalyses = filteredAnalyses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
          Analysis History
        </Typography>
        <Typography color="text.secondary">
          View and manage your previous cricket ball analyses
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h2" sx={{ mb: 1 }}>
              {stats.total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Analyses
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h2" sx={{ color: theme.palette.success.main, mb: 1 }}>
              {stats.averageSpeed}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Average Speed (km/h)
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.dark, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h2" sx={{ color: theme.palette.error.main, mb: 1 }}>
              {stats.fastest}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fastest Delivery (km/h)
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Controls */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search analyses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              component={Link}
              to="/analyze"
              variant="contained"
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              }}
            >
              New Analysis
            </Button>
            <Button
              variant="outlined"
              onClick={fetchAnalyses}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Analyses Table */}
      <Paper
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date (IST)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Deliveries</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Speed (km/h)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Processing Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAnalyses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom color="text.secondary">
                        No analyses found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchTerm ? 'Try a different search term' : 'Upload your first video to get started'}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          component={Link}
                          to="/analyze"
                          variant="contained"
                          sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          }}
                        >
                          Start Analysis
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAnalyses.map((analysis) => {
                  const firstDelivery = analysis.deliveries?.[0];
                  const averageSpeed = analysis.deliveries?.reduce(
                    (sum, delivery) => sum + (delivery.metrics?.speed_kmh || 0), 0
                  ) / (analysis.deliveries?.length || 1);

                  return (
                    <TableRow
                      key={analysis.analysis_id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {analysis.filename || 'Untitled'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {analysis.analysis_id?.slice(0, 8)}...
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarTodayIcon fontSize="small" color="primary" sx={{ opacity: 0.7 }} />
                          <Box>
                            <Typography variant="body2">
                              {formatToIST(analysis.timestamp)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTimeAgoIST(analysis.timestamp)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={`${analysis.deliveries?.length || 0} delivery${analysis.deliveries?.length !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SpeedIcon fontSize="small" color="primary" sx={{ opacity: 0.7 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {Math.round(averageSpeed)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            km/h
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        {firstDelivery?.delivery_type && (
                          <Chip
                            label={firstDelivery.delivery_type.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: `${getDeliveryTypeColor(firstDelivery.delivery_type)}20`,
                              color: getDeliveryTypeColor(firstDelivery.delivery_type),
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTimeIcon fontSize="small" color="primary" sx={{ opacity: 0.7 }} />
                          <Typography variant="body2">
                            {analysis.processing_time || 'N/A'}s
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <IconButton
                            component={Link}
                            to={`/results/${analysis.analysis_id}`}
                            size="small"
                            sx={{
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            sx={{
                              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                              },
                            }}
                            onClick={() => {
                              const dataStr = JSON.stringify(analysis, null, 2);
                              const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
                              const linkElement = document.createElement('a');
                              linkElement.setAttribute('href', dataUri);
                              linkElement.setAttribute('download', `cricket_analysis_${analysis.analysis_id}.json`);
                              linkElement.click();
                              toast.success('Analysis downloaded');
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          
                          <IconButton
                            size="small"
                            sx={{
                              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                            onClick={() => handleDeleteClick(analysis)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredAnalyses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        />
      </Paper>

      {/* Recent Analyses Grid View */}
      {analyses.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
            Recent Analyses
          </Typography>
          
          <Grid container spacing={3}>
            {analyses.slice(0, 4).map((analysis) => {
              const firstDelivery = analysis.deliveries?.[0];
              const averageSpeed = analysis.deliveries?.reduce(
                (sum, delivery) => sum + (delivery.metrics?.speed_kmh || 0), 0
              ) / (analysis.deliveries?.length || 1);

              return (
                <Grid item xs={12} sm={6} md={3} key={analysis.analysis_id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {formatTimeAgoIST(analysis.timestamp)}
                      </Typography>
                      
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {analysis.filename || 'Untitled'}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Speed
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {Math.round(averageSpeed)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            km/h
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Deliveries
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {analysis.deliveries?.length || 0}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {firstDelivery?.delivery_type && (
                        <Chip
                          label={firstDelivery.delivery_type.toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: `${getDeliveryTypeColor(firstDelivery.delivery_type)}20`,
                            color: getDeliveryTypeColor(firstDelivery.delivery_type),
                            fontWeight: 500,
                            mb: 2,
                          }}
                        />
                      )}
                      
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Processed in {analysis.processing_time || 'N/A'}s
                      </Typography>
                    </CardContent>
                    
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button
                        component={Link}
                        to={`/results/${analysis.analysis_id}`}
                        fullWidth
                        variant="outlined"
                        size="small"
                      >
                        View Details
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          
          {analyses.length > 4 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={Link}
                to="#"
                variant="text"
                onClick={(e) => {
                  e.preventDefault();
                  setPage(Math.floor(analyses.length / rowsPerPage));
                }}
              >
                View All Analyses
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Analysis</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{analysisToDelete?.filename || 'this analysis'}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default History;